import React, { useMemo } from "react";
import { buildDiscReport } from "./discReportEngine";
import { formatDuration } from "../../utils/formatDuration";
import { DISC_RENK, DISC_UZERI_YAZI, EKSENLER, CEYREK_DUZENI, HARF_ICERIK } from "../discBrand";

// Palet paylaşım belgesinden (src/disc/discBrand.js). Rapor ile admin grafiği
// aynı renkleri kullanır; ikinci bir palet TANIMLANMAZ.
const DISC_COLORS = DISC_RENK;

const DIFFERENCE_COLORS = {
  close: "#16803c",
  far: "#b45309",
  veryFar: "#b91c1c"
};

export default function DiscPersonalReport({
  adaptedScores,
  naturalScores,
  user,
  timestamp,
  durationMs,
  language = "tr",
  accessContext,
  baskiModu = false
}) {
  const lang = language === "en" ? "en" : "tr";
  const isTR = lang === "tr";
  const durationText = formatDuration(durationMs, lang);
  const participantName = [user?.name, user?.surname].filter(Boolean).join(" ").trim() || (isTR ? "Katılımcı" : "Participant");

  const report = useMemo(
    () =>
      buildDiscReport({
        adaptedScores,
        naturalScores,
        participant: {
          name: participantName,
          company: accessContext?.companyName || "",
          team: accessContext?.teamName || "",
          generationRange: user?.generationRange || "",
          roleLevel: user?.roleLevel || ""
        },
        language: lang
      }),
    [adaptedScores, naturalScores, participantName, accessContext, user, lang]
  );

  const text = getText(lang);

  return (
    <section style={styles.shell} lang={lang} data-report-profile={report.profile.code}>
      <ReportPage number={1} total={13} kicker={text.snapshotKicker} title={text.snapshotTitle} first>
        {!baskiModu && (
          <>
            <div style={styles.hero}>
              <p style={styles.eyebrow}>{text.eyebrow}</p>
              <h2 style={styles.title}>{text.title}</h2>
              <p style={styles.intro}>{text.intro}</p>
            </div>
            <div style={styles.metaGrid}>
              <Meta label={text.participant} value={participantName} />
              <Meta label={text.company} value={accessContext?.companyName || "-"} />
              <Meta label={text.team} value={accessContext?.teamName || "-"} />
              <Meta label={text.date} value={timestamp || "-"} />
              <Meta label={text.duration} value={durationText} />
            </div>
          </>
        )}

        {baskiModu && <p style={styles.pageLead}>{text.intro}</p>}

        <div style={styles.signature}>
          <span style={styles.signatureLabel}>{text.currentSignature}</span>
          <strong style={styles.signatureTitle}>{report.profileTitle}</strong>
          <div style={styles.signalRow}>
            {report.profile.orderedAll.map((item) => (
              <SignalChip
                key={item.key}
                item={item}
                active={item.value >= 50}
                near={report.profile.nearCluster.some((clusterItem) => clusterItem.key === item.key)}
                tr={isTR}
              />
            ))}
          </div>
        </div>

        <Card title={text.executive} featured>
          <p style={styles.paragraph}>{report.executiveSummary}</p>
        </Card>

        <div style={styles.twoColumn} className="sl-izgara">
          <Card title={text.combination}>
            <p style={styles.paragraph}>{report.combinationNarrative}</p>
          </Card>
          <Card title={text.decision}>
            <p style={styles.paragraph}>{report.decisionStyle}</p>
          </Card>
        </div>
      </ReportPage>

      <ReportPage number={2} total={13} kicker={text.architectureKicker} title={text.architectureTitle}>
        <p style={styles.pageLead}>{text.architectureLead}</p>
        <div style={styles.architectureGrid} className="sl-architecture-grid">
          {report.profileArchitecture.map((item) => (
            <ArchitectureCard key={item.key} item={item} />
          ))}
        </div>
        <Card title={text.profileDynamics} featured>
          <p style={styles.paragraph}>{report.combinationNarrative}</p>
        </Card>
        <Callout title={text.methodMiniTitle}>
          {report.methodology.activeRule} {report.methodology.clusterRule} {report.methodology.differenceRule}
        </Callout>
        <DiscBarPair adapted={report.adapted} natural={report.natural} text={text} />
      </ReportPage>

      <ReportPage number={3} total={13} kicker={text.adaptedKicker} title={text.adaptedTitle}>
        <p style={styles.pageLead}>{report.adaptedProfileSummary}</p>
        <ScorePanel title={text.adaptedScores} scores={report.adapted} tr={isTR} />
        <div style={styles.twoColumn} className="sl-izgara">
          <Card title={text.workStyle}>
            <p style={styles.paragraph}>{report.workStyle}</p>
          </Card>
          <ListCard title={text.strengths} items={report.strengths} />
        </div>
        <Card title={text.combinationReading} featured>
          <p style={styles.paragraph}>{report.combinationNarrative}</p>
        </Card>
      </ReportPage>

      <ReportPage number={4} total={13} kicker={text.naturalKicker} title={text.naturalTitle}>
        <p style={styles.pageLead}>{report.naturalProfileSummary}</p>
        <ScorePanel title={text.naturalScores} scores={report.natural} tr={isTR} />
        <DiscComboChart adapted={report.adapted} natural={report.natural} text={text} />
      </ReportPage>

      {/* Uyarlanma okuması ayrı sayfaya alındı: doğal profil sayfasında özet, çubuk
          panel ve birleşik grafik zaten yer kaplıyordu ve tablo taşıp yarım sayfa
          açıyordu. Fark anlatısı + harf harf tablo birlikte okunmalı. */}
      <ReportPage number={5} total={13} kicker={text.diffKicker} title={text.diffTitle}>
        <p style={styles.pageLead}>{text.diffLead}</p>
        <Card title={text.difference} featured>
          <p style={styles.paragraph}>{report.adaptationInsight}</p>
        </Card>
        <Card title={text.energyTitle} featured>
          <p style={styles.paragraph}>{report.adaptationEnergy}</p>
        </Card>
        <DifferenceTable rows={report.differences} text={text} tr={isTR} />
      </ReportPage>

      {/* 2026-08-17 · literatür katmanı sayfası: klasik DISC örüntüsü (uyarlanmış +
          doğal), Marston eksenleri ve uyarlama enerjisi. Raporu harf sıralamasının
          ötesine taşıyan bölüm budur. */}
      <ReportPage number={6} total={13} kicker={text.patternKicker} title={text.patternTitle}>
        <p style={styles.pageLead}>{text.patternLead}</p>
        <div style={styles.twoColumn} className="sl-izgara">
          {report.patternReading.map((blok) => (
            <PatternCard key={blok.key} blok={blok} />
          ))}
        </div>
      </ReportPage>

      {/* Paylaşım belgesinin "DISC Eksenleri" sayfasının rapor karşılığı: çeyrek
          haritası ve kişinin bu eksenlerdeki konumu birlikte okunur. */}
      <ReportPage number={7} total={13} kicker={text.axisKicker} title={text.axisPageTitle}>
        <p style={styles.pageLead}>{text.axisPageLead}</p>
        <DiscAxisMap lang={lang} text={text} />
        <Card title={text.axisTitle}>
          <p style={styles.paragraph}>{report.axisReading}</p>
        </Card>
      </ReportPage>

      <ReportPage number={8} total={13} kicker={text.dimensionsKicker} title={text.dimensionsTitle}>
        <p style={styles.pageLead}>{text.dimensionsLead}</p>
        <div style={styles.dimensionGrid}>
          {report.sections.map((section) => (
            <DimensionCard key={section.key} section={section} lang={lang} />
          ))}
        </div>
      </ReportPage>

      <ReportPage number={9} total={13} kicker={text.scenariosKicker} title={text.scenariosTitle}>
        <p style={styles.pageLead}>{text.scenariosLead}</p>
        <div style={styles.scenarioGrid} className="sl-scenario-grid">
          {report.scenarioReadings.map((item) => (
            <ScenarioCard key={item.key} item={item} />
          ))}
        </div>
      </ReportPage>

      <ReportPage number={10} total={13} kicker={text.communicationKicker} title={text.communicationTitle}>
        <p style={styles.pageLead}>{text.communicationLead}</p>
        <div style={styles.communicationGrid}>
          {report.communicationGuide.map((item) => (
            <CommunicationCard key={item.key} item={item} text={text} />
          ))}
        </div>
      </ReportPage>

      {/* Karşılıklı algı — kaynak: en15/en16 "IF YOU ARE A 'D' YOU ARE SEEN BY THE…".
          Kaynak saf sıfat listesidir ve tamamı olumsuzdur; burada yargı değil ALGI
          anlatılır ve her okumanın yanına kapatıcı adım konur (bkz. discAlgi.js). */}
      <ReportPage number={11} total={13} kicker={text.perceptionKicker} title={text.perceptionTitle}>
        <p style={styles.pageLead}>{text.perceptionLead}</p>
        <div style={styles.twoColumn} className="sl-izgara">
          {report.perceptionReadings.map((o) => (
            <Card
              key={o.karsi}
              title={`${o.karsi}${o.kendi ? ` · ${text.perceptionSelf}` : ""}`}
            >
              <p style={styles.algiEtiket}>{text.perceptionRead}</p>
              <p style={styles.algiMetin}>{o.okunus}</p>
              <p style={styles.algiEtiket}>{text.perceptionBridge}</p>
              <p style={styles.algiMetin}>{o.kopru}</p>
            </Card>
          ))}
        </div>
      
        {/* TAKIM ETKİSİ — referansın 8. sayfasındaki "Takım etkisi" kutusunun karşılığı.
            Karşılıklı algının hemen altında: "sizi nasıl görürler" + "kim sizi tamamlar". */}
        {report.teamFit ? (
          <div style={styles.takimKutu} className="sl-blok">
            <p style={styles.takimUst}>{text.teamFitKicker}</p>
            <h4 style={styles.takimBaslik}>{text.teamFitTitle}</h4>
            <p style={styles.takimIntro}>{report.teamFit.intro}</p>

            <div style={styles.takimIzgara} className="sl-izgara">
              <div style={{ ...styles.takimKart, borderTopColor: DISC_COLORS[report.teamFit.tamamlayan] }}>
                <strong style={styles.takimKartBaslik}>{report.teamFit.tamamlayanBaslik}</strong>
                <p style={styles.takimKartMetin}>{report.teamFit.tamamlayanMetin}</p>
                <p style={styles.takimGerilim}>{report.teamFit.gerilim}</p>
              </div>
              <div style={{ ...styles.takimKart, borderTopColor: DISC_COLORS[report.teamFit.kolay] }}>
                <strong style={styles.takimKartBaslik}>{report.teamFit.kolayBaslik}</strong>
                <p style={styles.takimKartMetin}>{report.teamFit.kolayMetin}</p>
              </div>
              {report.teamFit.birlesik ? null : (
                <div style={{ ...styles.takimKart, borderTopColor: DISC_COLORS[report.teamFit.capraz] }}>
                  <strong style={styles.takimKartBaslik}>{report.teamFit.caprazBaslik}</strong>
                  <p style={styles.takimKartMetin}>{report.teamFit.caprazMetin}</p>
                </div>
              )}
            </div>

            <p style={styles.takimSoru}>
              <span style={styles.takimSoruEtiket}>{report.teamFit.soruEtiketi}</span>
              {report.teamFit.soru}
            </p>
          </div>
        ) : null}
      </ReportPage>

      <ReportPage number={12} total={13} kicker={text.actionKicker} title={text.actionTitle}>
        <DevelopmentPlan items={report.developmentPlan} />
        <ListCard title={text.ownTips} items={report.communicationTips} />
        <div style={styles.twoColumn} className="sl-izgara">
          <ListCard title={text.manager} items={report.managerNotes} />
          <ListCard title={text.watchouts} items={report.watchouts} />
        </div>
      </ReportPage>

      {/* Referansın 9. sayfası "C-level ve İK okuma lensi"dir. Motor hrNotes ve
          evidenceNotes'u üretiyordu ama hiçbir yerde basılmıyordu; sayfa geri kondu. */}
      <ReportPage number={13} total={13} kicker={text.lensKicker} title={text.lensTitle}>
        <p style={styles.pageLead}>{text.lensLead}</p>
        <div style={styles.twoColumn} className="sl-izgara">
          <ListCard title={text.hr} items={report.hrNotes} />
          <ListCard title={text.evidence} items={report.evidenceNotes} />
        </div>
        <DiscUseNotes text={text} />
        <p style={styles.note}>{text.note}</p>
      </ReportPage>
    </section>
  );
}

function PatternCard({ blok }) {
  return (
    <article style={styles.card}>
      <h3 style={styles.cardTitle}>{blok.title}</h3>
      <p style={styles.patternHeading}>{blok.heading}</p>
      <p style={styles.paragraph}>{blok.body}</p>
    </article>
  );
}

function getText(lang) {
  if (lang === "en") {
    return {
      eyebrow: "SOMEREN LABS · DISC",
      title: "Personalised Behaviour Report",
      intro: "This report interprets the participant’s natural and adapted DISC results in relation to current work behaviour, communication, decision-making and development. It is designed to open a grounded conversation, not to label a person.",
      participant: "Participant",
      company: "Company",
      team: "Team",
      date: "Date",
      duration: "Completion Time",
      perceptionKicker: "HOW IT LANDS",
      perceptionTitle: "The Same Behaviour, Four Different Readings",
      perceptionLead: "Your lead signal does not land the same way on everyone. The reading below is not about who you are — it is about how the same behaviour can be interpreted by someone whose own signal is different, and what closes that gap.",
      perceptionSelf: "same signal",
      perceptionRead: "How it can read",
      perceptionBridge: "What closes the gap",
      snapshotKicker: "CURRENT PROFILE",
      snapshotTitle: "Executive Profile Signature",
      currentSignature: "50+ adapted style order",
      executive: "Executive Summary",
      combination: "Combination Dynamics",
      decision: "Decision Lens",
      architectureKicker: "YOUR PROFILE FINGERPRINT",
      architectureTitle: "What Makes This Pattern Distinctive",
      architectureLead: "The four cards below use the exact lead, support, gap, lowest channel and natural/adapted movement in this result. They are not type-level descriptions.",
      profileDynamics: "How This Exact Pattern Works",
      methodMiniTitle: "Transparent interpretation rule",
      methodKicker: "HOW TO READ",
      methodTitle: "Method and Interpretation Rules",
      activeRule: "Active style rule",
      clusterRule: "Near-cluster rule",
      differenceRule: "Difference rule",
      frameworkNote: "Someren Labs framework note",
      frameworkBody: "The report uses transparent house thresholds to make interpretation consistent. These thresholds personalise narrative; they do not change responses, scoring calculations or the existing charts.",
      adaptedKicker: "CURRENT STATE",
      adaptedTitle: "Adapted Profile in Depth",
      adaptedScores: "Adapted Profile",
      workStyle: "Current Work Style",
      strengths: "Visible Contributions",
      combinationReading: "How the Active Styles Work Together",
      naturalKicker: "FOUNDATIONAL STATE",
      naturalTitle: "Natural Profile and Adaptation",
      naturalScores: "Natural Profile",
      difference: "Natural / Adapted Difference",
      adaptedColumn: "Adapted",
      naturalColumn: "Natural",
      deltaColumn: "Difference",
      readingColumn: "Reading",
      dimensionsKicker: "FOUR SIGNALS",
      dimensionsTitle: "Dimension-by-Dimension Interpretation",
      dimensionsLead: "Each dimension is interpreted using its own score band, its natural level and the direction and distance of adaptation. A lower score means lower visibility, not lack of value.",
      impactKicker: "WORK IMPACT",
      impactTitle: "Strengths, Risks and Organisational Use",
      scenariosKicker: "SIX WORK MOMENTS",
      scenariosTitle: "How This Profile Appears in Real Work",
      scenariosLead: "The readings below combine the lead style, closest support channel, lowest channel and numerical gaps for six recurring work situations.",
      watchouts: "Watch-outs",
      communication: "Self-Management in Communication",
      manager: "Manager Lens",
      hr: "HR Lens",
      diffKicker: "THE GAP",
      diffTitle: "Distance Between Natural and Adapted",
      diffLead: "This page reads the gap shown in the combined chart letter by letter: which behaviour is more visible today and which is held back.",
      axisKicker: "BEHAVIOUR MAP",
      axisPageTitle: "Where Do the Letters Come From?",
      axisPageLead: "In DISC, a letter is not a label on its own. Two axes are read together: working pace and the focus a person prioritises. Their intersection shows the dominant tendency.",
      axisMapTitle: "Two axes, four behavioural lenses",
      axisMapNote: "The letters are not labels on their own. The vertical axis asks about pace (fast or calm) and the horizontal axis about focus (task or people); the intersection shows the dominant tendency. The quoted phrases are what that tendency most often sounds like in a meeting.",
      pairTitle: "Adapted and natural profile — side by side",
      pairNote: "The left chart shows the current state and the right one the baseline. Solid bars are adapted and hatched bars natural; colour intensity is identical on both sides. Dashed lines mark the 45 and 75 band thresholds.",
      comboTitle: "Natural and adapted profile — single-axis comparison",
      comboAdapted: "Adapted (current state)",
      comboNatural: "Natural (baseline state)",
      comboUp: "more visible when adapted",
      comboDown: "more moderated when adapted",
      comboNote: "The dashed line is the natural profile and the solid line the adapted one; the vertical leg shows the size of the difference. Dashed horizontal lines mark the 45 and 75 band thresholds. Direction is not a performance measure: rising is not \u201cgood\u201d and falling is not \u201cbad\u201d.",
      patternKicker: "PATTERN READING",
      patternTitle: "Classical DISC pattern and behavioural axis",
      patternLead: "This page reads the scores as a shape rather than four separate numbers: which classical DISC pattern the profile approaches, and whether the natural and adapted graphs land on the same one.",
      axisTitle: "Behavioural axis (Marston)",
      energyTitle: "Adaptation energy",
      ownTips: "For Your Own Communication",
      teamFitKicker: "TEAM EFFECT",
      teamFitTitle: "Who completes you",
      lensKicker: "C-LEVEL AND HR LENS",
      lensTitle: "How should this report be read organisationally?",
      lensLead: "Someren Labs positions this report for development conversations, team complementarity and culture visibility, not for labelling people.",
      evidence: "Source and Method Notes",
      communicationKicker: "PRACTICAL APPLICATION",
      communicationTitle: "How to Communicate with D, I, S and C",
      communicationLead: "The guidance below starts from this participant’s adapted lead styles and then adjusts pace, tone and detail for the other person.",
      needLabel: "What they need",
      doLabel: "Do",
      avoidLabel: "Avoid",
      personalLabel: "Your personalised adjustment",
      actionKicker: "NEXT 30 DAYS",
      actionTitle: "Your Personal Development Experiments",
      evidence: "Research and Framework Notes",
      overviewTitle: "DISC overview",
      overviewLead: "Four observable behaviour signals",
      overviewIntro: "Someren Labs uses brand-specific names while retaining the familiar D, I, S and C letters.",
      dimD: ["Direct", "Results, speed, decisions and direction."],
      dimI: ["Influencer / Expressive", "Communication, persuasion, visible energy and connection."],
      dimS: ["Steadfast / Patient", "Continuity, patience, support and trust."],
      dimC: ["Cautious / Serious", "Accuracy, evidence, quality and risk control."],
      useTitle: "Responsible use",
      safeItems: [
        "Use the report to start development and communication conversations, not to fix a person into a type.",
        "Do not use DISC as a clinical assessment, ability test or standalone hiring decision.",
        "Validate every interpretation with the participant’s real examples and context."
      ],
      note: "DISC results are intended for development, awareness, communication and teamwork. They should not be used as a diagnosis, selection verdict or standalone performance judgement."
    };
  }

  return {
    eyebrow: "SOMEREN LABS · DISC",
    title: "Kişiselleştirilmiş Davranış Raporu",
    intro: "Bu rapor, katılımcının doğal ve uyarlanmış DISC sonuçlarını mevcut iş davranışı, iletişim, karar alma ve gelişim odağı açısından yorumlar. Amaç kişiyi etiketlemek değil, veriye dayalı bir gelişim konuşması açmaktır.",
    participant: "Katılımcı",
    company: "Şirket",
    team: "Takım",
    date: "Tarih",
    duration: "Tamamlama Süresi",
    perceptionKicker: "KARŞIDA NASIL İNİYOR",
    perceptionTitle: "Aynı Davranış, Dört Farklı Okuma",
    perceptionLead: "Ana sinyaliniz herkese aynı şekilde inmez. Aşağıdaki okuma kim olduğunuzla ilgili değildir; aynı davranışın, sinyali farklı olan biri tarafından nasıl yorumlanabileceğini ve aradaki farkı neyin kapattığını anlatır.",
    perceptionSelf: "aynı sinyal",
    perceptionRead: "Nasıl okunabilir",
    perceptionBridge: "Farkı ne kapatır",
    snapshotKicker: "MEVCUT PROFİL",
    snapshotTitle: "Yönetici Özeti ve Profil İmzası",
    currentSignature: "%50+ uyarlanmış stil sırası",
    executive: "Yönetici Özeti",
    combination: "Kombinasyon Dinamiği",
    decision: "Karar Alma Merceği",
    architectureKicker: "SİZE ÖZGÜ PROFİL PARMAK İZİ",
    architectureTitle: "Bu Örüntüyü Benzersiz Kılan Yapı",
    architectureLead: "Aşağıdaki dört okuma yalnız stil adına değil; bu sonucun liderine, destek kanalına, puan açıklıklarına, en düşük kanalına ve doğal/uyarlanmış hareketine dayanır.",
    profileDynamics: "Bu Sayısal Yapı Birlikte Nasıl Çalışıyor?",
    methodMiniTitle: "Şeffaf yorumlama kuralı",
    methodKicker: "NASIL OKUNUR?",
    methodTitle: "Yöntem ve Yorumlama Kuralları",
    activeRule: "Ana stil kuralı",
    clusterRule: "Yakın küme kuralı",
    differenceRule: "Fark kuralı",
    frameworkNote: "Someren Labs çerçeve notu",
    frameworkBody: "Rapor, yorumların tutarlı olması için şeffaf kurum eşikleri kullanır. Bu eşikler yalnız anlatımı kişiselleştirir; cevapları, skor hesaplamasını veya mevcut grafikleri değiştirmez.",
    adaptedKicker: "ŞU ANKİ HAL",
    adaptedTitle: "Uyarlanmış Profilin Derin Okuması",
    adaptedScores: "Uyarlanmış Profil",
    workStyle: "Mevcut Çalışma Tarzı",
    strengths: "Görünür Katkılar",
    combinationReading: "Aktif Stiller Birlikte Nasıl Çalışıyor?",
    naturalKicker: "TEMEL HAL",
    naturalTitle: "Doğal Profil ve Uyarlanma",
    naturalScores: "Doğal Profil",
    difference: "Doğal / Uyarlanmış Fark",
    adaptedColumn: "Uyarlanmış",
    naturalColumn: "Doğal",
    deltaColumn: "Fark",
    readingColumn: "Yorum",
    dimensionsKicker: "DÖRT SİNYAL",
    dimensionsTitle: "Boyut Boyut Kişiselleştirilmiş Yorum",
    dimensionsLead: "Her boyut kendi yoğunluk bandı, doğal düzeyi ve uyarlanmanın yönü ile mesafesi üzerinden okunur. Düşük skor değersizlik değil, daha düşük görünürlük demektir.",
    impactKicker: "İŞE ETKİ",
    impactTitle: "Güçlü Yanlar, Riskler ve Kurumsal Okuma",
    scenariosKicker: "ALTI İŞ ANI",
    scenariosTitle: "Bu Profil Gerçek İşte Nasıl Görünür?",
    scenariosLead: "Aşağıdaki okumalar altı sık iş durumunda lider stili, en yakın destek kanalını, en düşük kanalı ve aralarındaki sayısal açıklığı birlikte kullanır.",
    watchouts: "Dikkat Edilecek Noktalar",
    communication: "İletişimde Öz Yönetim",
    manager: "Yönetici İçin Okuma",
    hr: "İK İçin Okuma",
    diffKicker: "ARADAKİ FARK",
    diffTitle: "Doğal ile Uyarlanmış Arasındaki Mesafe",
    diffLead: "Bu sayfa, önceki sayfadaki birleşik grafikte görünen farkı harf harf okur: hangi davranış bugün daha görünür, hangisi daha ölçülü kullanılıyor.",
    axisKicker: "DAVRANIŞ HARİTASI",
    axisPageTitle: "Harfler Nereden Geliyor?",
    axisPageLead: "DISC yorumunda harfler tek başına bir etiket değildir. İki eksen birlikte okunur: kişinin çalışma temposu ve öncelik verdiği odak. Kesişim, baskın davranış eğilimini gösterir.",
    axisMapTitle: "İki eksen, dört davranış merceği",
    axisMapNote: "Harfler tek başına bir etiket değildir. Dikey eksen tempoyu (hızlı mı, sakin mi), yatay eksen odağı (görev mi, insan mı) sorar; kesişim baskın davranış eğilimini gösterir. Tırnak içindeki cümleler, o eğilimin toplantıda en sık duyulan sesidir.",
    pairTitle: "Uyarlanmış ve doğal profil — yan yana",
    pairNote: "Soldaki grafik şu anki hâli, sağdaki temel hâli gösterir. Düz çubuklar uyarlanmış, taramalı çubuklar doğal profildir; iki tarafta da renkler aynı yoğunluktadır. Kesikli yatay çizgiler 45 ve 75 bant sınırlarıdır.",
    comboTitle: "Doğal ve uyarlanmış profil — tek eksende karşılaştırma",
    comboAdapted: "Uyarlanmış (şu anki hâl)",
    comboNatural: "Doğal (temel hâl)",
    comboUp: "uyarlanmışta daha görünür",
    comboDown: "uyarlanmışta daha ölçülü",
    comboNote: "Kesikli çizgi doğal, dolu çizgi uyarlanmış profildir; aradaki dikey bacak farkın büyüklüğünü gösterir. Kesikli yatay çizgiler 45 ve 75 bant sınırlarıdır. Yön bir başarı ölçüsü değildir: yükselmek \u201ciyi\u201d, alçalmak \u201ckötü\u201d anlamına gelmez; yalnızca mevcut bağlamın hangi davranışı daha görünür istediğini gösterir.",
    patternKicker: "ÖRÜNTÜ OKUMASI",
    patternTitle: "Klasik DISC örüntüsü ve davranış ekseni",
    patternLead: "Bu sayfa, skorları tek tek değil biçim olarak okur: profilin klasik DISC literatüründeki hangi örüntüye yaklaştığını, doğal ve uyarlanmış grafiklerin aynı örüntüye oturup oturmadığını gösterir.",
    axisTitle: "Davranış ekseni (Marston)",
    energyTitle: "Uyarlama enerjisi",
    ownTips: "Kendi İletişiminiz İçin",
    teamFitKicker: "TAKIM ETKİSİ",
    teamFitTitle: "Sizi kim tamamlar",
    lensKicker: "C-LEVEL VE İK LENSİ",
    lensTitle: "Bu rapor kurumsal olarak nasıl okunmalı?",
    lensLead: "Someren Labs bu raporu etiket üretmek için değil; gelişim konuşması, takım tamamlayıcılığı ve kültür görünürlüğü için konumlandırır.",
    evidence: "Kaynak ve Yöntem Notları",
    communicationKicker: "PRATİK UYGULAMA",
    communicationTitle: "D, I, S ve C ile Nasıl İletişim Kurmalı?",
    communicationLead: "Aşağıdaki öneriler bu katılımcının uyarlanmış ana stillerinden başlar; karşı taraf için tempo, ton ve ayrıntı düzeyini özel olarak ayarlar.",
    needLabel: "İhtiyacı",
    doLabel: "Yapın",
    avoidLabel: "Kaçının",
    personalLabel: "Size özel ayar",
    actionKicker: "ÖNÜMÜZDEKİ 30 GÜN",
    actionTitle: "Size Özel Gelişim Deneyleri",
    evidence: "Araştırma ve Çerçeve Notları",
    overviewTitle: "DISC genel bakış",
    overviewLead: "Dört gözlenebilir davranış sinyali",
    overviewIntro: "Someren Labs, tanıdık D, I, S ve C harflerini korurken kuruma özgü ve Türkçede daha doğal karşılıklar kullanır.",
    dimD: ["Direct / Direk", "Sonuç, hız, karar ve yön verme."],
    dimI: ["Influencer / İfadeci / İz Bırakan", "İletişim, ikna, görünür enerji ve bağ kurma."],
    dimS: ["Steadfast / Sadık / Sabırlı", "Süreklilik, sabır, destek ve güven."],
    dimC: ["Cautious / Ciddi", "Doğruluk, kanıt, kalite ve risk kontrolü."],
    useTitle: "Sorumlu kullanım",
    safeItems: [
      "Raporu kişiyi bir tipe sabitlemek için değil, gelişim ve iletişim konuşması başlatmak için kullanın.",
      "DISC sonucunu klinik değerlendirme, yetenek testi veya tek başına işe alım kararı yerine koymayın.",
      "Her yorumu katılımcının gerçek örnekleri ve bağlamıyla birlikte doğrulayın."
    ],
    note: "DISC sonuçları gelişim, farkındalık, iletişim ve takım çalışması amacıyla kullanılmalıdır. Teşhis, seçim hükmü veya tek başına performans yargısı değildir."
  };
}

function ReportPage({ number, total, kicker, title, children, first = false }) {
  return (
    <section
      className={`sl-report-page sl-blok${first ? " sl-report-page-first" : ""}`}
      style={styles.reportPage}
      data-page={number}
    >
      <header style={styles.pageHeader}>
        <div>
          <span style={styles.pageKicker}>{kicker}</span>
          <h3 style={styles.pageTitle}>{title}</h3>
        </div>
        <span style={styles.pageNumber}>{String(number).padStart(2, "0")} / {String(total).padStart(2, "0")}</span>
      </header>
      {children}
    </section>
  );
}

function Meta({ label, value }) {
  return (
    <div style={styles.meta}>
      <span style={styles.metaLabel}>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

// Yüzde işaretinin yeri dile bağlı: Türkçede sayının ÖNÜNDE (%53),
// İngilizcede ARKASINDA (53%). Tek biçim sabit yazılıydı; bir dilde yanlıştı.
function SignalChip({ item, active, near, tr }) {
  return (
    <div
      style={{
        ...styles.signalChip,
        borderColor: active ? DISC_COLORS[item.key] : "#d1d5db",
        opacity: active ? 1 : 0.62
      }}
    >
      <span style={{ ...styles.signalDot, background: DISC_COLORS[item.key] }}>{item.key}</span>
      <strong>{tr ? `%${item.value}` : `${item.value}%`}</strong>
      {near && <span style={styles.nearMark}>≈</span>}
    </div>
  );
}

function Card({ title, children, featured = false }) {
  return (
    <article style={{ ...styles.card, ...(featured ? styles.featuredCard : {}) }}>
      <h4 style={styles.cardTitle}>{title}</h4>
      {children}
    </article>
  );
}

function ListCard({ title, items }) {
  return (
    <Card title={title}>
      <ul style={styles.list}>
        {items.map((item, index) => <li key={index} style={styles.listItem}>{item}</li>)}
      </ul>
    </Card>
  );
}

function ArchitectureCard({ item }) {
  return (
    <article style={styles.architectureCard}>
      <span style={styles.architectureKey}>{item.title}</span>
      <p style={styles.architectureBody}>{item.body}</p>
    </article>
  );
}

function ScenarioCard({ item }) {
  return (
    <article style={styles.scenarioCard} className="sl-scenario-card">
      <h4 style={styles.cardTitle}>{item.title}</h4>
      <p style={styles.scenarioText}>{item.text}</p>
      <p style={styles.scenarioFocus}>{item.focus}</p>
    </article>
  );
}

function Callout({ title, children }) {
  return (
    <div style={styles.callout}>
      <strong style={styles.calloutTitle}>{title}</strong>
      <p style={styles.calloutText}>{children}</p>
    </div>
  );
}

function ScorePanel({ title, scores, tr }) {
  return (
    <article style={styles.scorePanel}>
      <h4 style={styles.cardTitle}>{title}</h4>
      {["D", "I", "S", "C"].map((key) => (
        <div key={key} style={styles.scoreRow}>
          <span style={{ ...styles.discBadge, background: DISC_COLORS[key] }}>{key}</span>
          <div style={styles.scoreTrack}>
            <div style={{ ...styles.scoreFill, width: `${scores[key]}%`, background: DISC_COLORS[key] }} />
          </div>
          <strong style={styles.scoreValue}>{tr ? `%${scores[key]}` : `${scores[key]}%`}</strong>
        </div>
      ))}
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DOĞAL + UYARLANMIŞ BİRLEŞİK GRAFİK (2026-08-17)
//
// Antarktika gelişim raporundaki "1. oturum → 2. oturum" çizgi karşılaştırmasının
// DISC karşılığı: iki grafik ayrı ayrı değil, TEK eksende üst üste okunur ve
// aradaki fark her harf için açıkça yazılır.
//
// Saf SVG: rapor tarayıcı baskısıyla PDF'e gidiyor, bu yüzden vektörel kalmalı ve
// harici grafik kütüphanesi kullanılmamalı (canvas rasterleşir, Türkçe fontu bozar).
//
// RENK KARARI: Antarktika'da delta yeşil/kırmızıydı çünkü orada artış İYİdir.
// DISC'te yön bir başarı ölçüsü DEĞİLDİR — motorun kendi cümlesi de bunu söyler
// ("yön tek başına iyi veya kötü değildir"). Bu yüzden delta rozetleri iyi/kötü
// değil, YÖN bildiren nötr renklerdedir.
// ─────────────────────────────────────────────────────────────────────────────
// YAN YANA DİKEY ÇUBUK ÇİFTİ (2026-08-17)
//
// Katılımcı testi bitirdiğinde ekranda uyarlanmış ve doğal profili dikey çubuk
// grafik olarak yan yana görüyordu (App.js step 3, Chart.js Bar). Bu görünüm
// rapora/PDF'e hiç girmiyordu; rapor yalnız yatay çubuk panelleri ayrı sayfalarda
// gösteriyordu. Buradaki SVG sürüm o karşılaştırmayı rapora taşır.
//
// Chart.js DEĞİL saf SVG: rapor tarayıcı baskısıyla PDF'e gidiyor; canvas tabanlı
// grafik rasterleşip baskıda bulanıklaşır.
// ─────────────────────────────────────────────────────────────────────────────
// DISC EKSEN HARİTASI (2026-08-18)
// Kaynak: SomerenLabs_DISC_Genel_Paylasim_Belgesi, sayfa 3 "DISC Eksenleri".
// Dikey eksen TEMPO (hızlı ↔ sakin), yatay eksen ODAK (görev ↔ insan).
// Çeyrekler: sol-üst D · sağ-üst I · sol-alt C · sağ-alt S.
// Bu düzen grup dağılım grafiğiyle (ClassAnalysis) BİREBİR aynıdır; ikisi de
// src/disc/discBrand.js dosyasından okur.
// Paylaşım belgesindeki ofis objeleri (D kronometre · I megafon · C kontrol listesi
// · S çaydanlık). Emoji KULLANILMAZ: tarayıcı baskısında emoji tek renge düşüp
// grileşiyor. Her ikon harfin marka renginde vektörel çizilir.
function ObjeIkon({ harf }) {
  const r = DISC_RENK[harf];
  const ortak = { width: 22, height: 22, viewBox: "0 0 24 24", fill: "none", "aria-hidden": true };
  if (harf === "D") {
    return (
      <svg {...ortak}>
        <circle cx="12" cy="13.5" r="7.5" fill={r} />
        <rect x="10" y="2.4" width="4" height="2.6" rx="1" fill={r} />
        <path d="M12 9.5v4.2l2.6 1.8" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (harf === "I") {
    return (
      <svg {...ortak}>
        <path d="M4 10.2v3.6l3.2.7 8.6 3.9V5.6L7.2 9.5 4 10.2Z" fill={r} />
        <path d="M18.4 8.6c1.4 1.9 1.4 5 0 6.8" stroke={r} strokeWidth="1.8" strokeLinecap="round" />
        <rect x="7" y="14.4" width="2.8" height="5.4" rx="1.2" fill={r} />
      </svg>
    );
  }
  if (harf === "C") {
    return (
      <svg {...ortak}>
        <rect x="4.4" y="3.6" width="15.2" height="17" rx="2.4" fill={r} />
        <rect x="8.6" y="1.9" width="6.8" height="3.4" rx="1.5" fill="#fff" stroke={r} strokeWidth="1.2" />
        <path d="M8 11.4l2 2 4-4.2" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8 16.6h8" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg {...ortak}>
      <path d="M5.5 11.5h10.2v4.2a4 4 0 0 1-4 4H9.5a4 4 0 0 1-4-4v-4.2Z" fill={r} />
      <path d="M15.9 12.6l3.4-2.1v4.6l-3.4-1.6" fill={r} />
      <rect x="8.2" y="8.6" width="5" height="2.2" rx="1.1" fill={r} />
      <circle cx="10.7" cy="6.7" r="1.5" fill={r} />
    </svg>
  );
}

function DiscAxisMap({ lang, text }) {
  const ic = HARF_ICERIK[lang === "en" ? "en" : "tr"];
  const eks = EKSENLER[lang === "en" ? "en" : "tr"];

  return (
    <figure style={styles.chartWrap} className="sl-blok">
      <figcaption style={styles.chartTitle}>{text.axisMapTitle}</figcaption>

      <div style={styles.eksenUstEtiket}>{eks.dikeyUst}</div>
      <div style={styles.eksenSatir}>
        <div style={styles.eksenYanEtiket}>{eks.yatakSol}</div>
        <div style={styles.ceyrekIzgara}>
          {CEYREK_DUZENI.map((k) => (
            <div key={k} style={styles.ceyrekHucre}>
              <div style={styles.ceyrekUst}>
                <span style={{ ...styles.ceyrekRozet, background: DISC_RENK[k], color: DISC_UZERI_YAZI[k] }}>{k}</span>
                <strong style={styles.ceyrekAd}>{ic[k].ad}</strong>
                <span style={styles.ceyrekObje} title={ic[k].objeAd}><ObjeIkon harf={k} /></span>
              </div>
              <div style={styles.ceyrekEksen}>{ic[k].eksen}</div>
              <div style={styles.ceyrekAnahtar}>{ic[k].anahtar}</div>
              <div style={{ ...styles.ceyrekSes, borderColor: DISC_RENK[k] }}>“{ic[k].ses}”</div>
            </div>
          ))}
        </div>
        <div style={styles.eksenYanEtiket}>{eks.yatakSag}</div>
      </div>
      <div style={styles.eksenUstEtiket}>{eks.dikeyAlt}</div>

      <p style={styles.chartNote}>{text.axisMapNote}</p>
    </figure>
  );
}

// Katılımcının envanteri bitirince gördüğü kadarını tek başına basan sarmalayıcı.
// Admin'deki "Katılımcı PDF" düğmesi de bunu kullanır: katılımcıya ne gösteriliyorsa
// o PDF'te de tam olarak o vardır, fazlası yoktur.
export function KatilimciGrafikleri({ adapted, natural, language = "tr" }) {
  const lang = language === "en" ? "en" : "tr";
  return <DiscBarPair adapted={adapted} natural={natural} text={getText(lang)} />;
}

function DiscBarPair({ adapted, natural, text }) {
  const harfler = ["D", "I", "S", "C"];

  function MiniGrafik({ skorlar, baslik, vurgulu }) {
    const G = 330, Y = 200;
    const solB = 28, sagB = 10, ustB = 18, altB = 30;
    const cizG = G - solB - sagB;
    const cizY = Y - ustB - altB;
    const yKonum = (v) => ustB + cizY - (Math.max(0, Math.min(100, v)) / 100) * cizY;
    const sutunG = cizG / harfler.length;
    const cubukG = Math.min(38, sutunG * 0.52);

    return (
      <div style={styles.miniWrap}>
        <div style={{ ...styles.miniTitle, color: vurgulu ? "#08060d" : "#4b5563" }}>{baslik}</div>
        <svg viewBox={`0 0 ${G} ${Y}`} width="100%" style={{ display: "block", overflow: "visible" }}>
          {/* Doğal profili soluklaştırmak yerine desenle ayırıyoruz: marka renkleri
              tam doygunlukta kalsın. */}
          <defs>
            <pattern id="sl-tarama" patternUnits="userSpaceOnUse" width="7" height="7"
                     patternTransform="rotate(45)">
              <rect width="7" height="7" fill="none" />
              <line x1="0" y1="0" x2="0" y2="7" stroke="#ffffff" strokeWidth="3.2" strokeOpacity="0.85" />
            </pattern>
          </defs>
          {[0, 25, 50, 75, 100].map((v) => (
            <g key={v}>
              <line x1={solB} y1={yKonum(v)} x2={G - sagB} y2={yKonum(v)} stroke="#eceff3" strokeWidth="1" />
              <text x={solB - 5} y={yKonum(v) + 3} textAnchor="end" fontSize="8.5" fill="#94a3b8">{v}</text>
            </g>
          ))}
          {/* bant sınırları: referans "> 75 yüksek · 45-75 orta · < 45 düşük" */}
          {[45, 75].map((v) => (
            <line key={v} x1={solB} y1={yKonum(v)} x2={G - sagB} y2={yKonum(v)}
                  stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 3" />
          ))}

          {harfler.map((k, i) => {
            const x = solB + sutunG * i + (sutunG - cubukG) / 2;
            const y = yKonum(skorlar[k]);
            const h = Math.max(1, ustB + cizY - y);
            return (
              <g key={k}>
                <rect x={x} y={y} width={cubukG} height={h} rx="3" fill={DISC_COLORS[k]} />
                {!vurgulu && (
                  <rect x={x} y={y} width={cubukG} height={h} rx="3" fill="url(#sl-tarama)" />
                )}
                <text x={x + cubukG / 2} y={y - 5} textAnchor="middle"
                      fontSize="11" fontWeight="800" fill="#0f172a">{skorlar[k]}</text>
                <text x={x + cubukG / 2} y={Y - altB + 17} textAnchor="middle"
                      fontSize="13" fontWeight="800" fill={DISC_COLORS[k]}>{k}</text>
              </g>
            );
          })}
          <line x1={solB} y1={ustB + cizY} x2={G - sagB} y2={ustB + cizY} stroke="#cbd5e1" strokeWidth="1.5" />
        </svg>
      </div>
    );
  }

  return (
    <figure style={styles.chartWrap} className="sl-blok">
      <figcaption style={styles.chartTitle}>{text.pairTitle}</figcaption>
      <div style={styles.pairRow}>
        <MiniGrafik skorlar={adapted} baslik={text.comboAdapted} vurgulu />
        <MiniGrafik skorlar={natural} baslik={text.comboNatural} />
      </div>
      <p style={styles.chartNote}>{text.pairNote}</p>
    </figure>
  );
}

function DiscComboChart({ adapted, natural, text }) {
  const harfler = ["D", "I", "S", "C"];
  const G = 700, Y = 205;
  const solB = 40, sagB = 18, ustB = 22, altB = 36;
  const cizG = G - solB - sagB;
  const cizY = Y - ustB - altB;

  const yKonum = (v) => ustB + cizY - (Math.max(0, Math.min(100, v)) / 100) * cizY;
  const xKonum = (i) => solB + (cizG / harfler.length) * (i + 0.5);

  const dogalNok = harfler.map((k, i) => `${xKonum(i)},${yKonum(natural[k])}`).join(" ");
  const uyarNok = harfler.map((k, i) => `${xKonum(i)},${yKonum(adapted[k])}`).join(" ");

  return (
    <figure style={styles.chartWrap} className="sl-blok">
      <figcaption style={styles.chartTitle}>{text.comboTitle}</figcaption>

      <svg viewBox={`0 0 ${G} ${Y}`} width="100%" role="img" aria-label={text.comboTitle}
           style={{ display: "block", overflow: "visible" }}>
        {/* yatay ızgara + bant sınırları (referans: >75 yüksek, 45-75 orta, <45 düşük) */}
        {[0, 25, 50, 75, 100].map((v) => (
          <g key={v}>
            <line x1={solB} y1={yKonum(v)} x2={G - sagB} y2={yKonum(v)}
                  stroke={v === 45 || v === 75 ? "#cbd5e1" : "#eceff3"} strokeWidth="1" />
            <text x={solB - 7} y={yKonum(v) + 3.5} textAnchor="end"
                  fontSize="10" fill="#94a3b8">{v}</text>
          </g>
        ))}
        {[45, 75].map((v) => (
          <line key={v} x1={solB} y1={yKonum(v)} x2={G - sagB} y2={yKonum(v)}
                stroke="#cbd5e1" strokeWidth="1" strokeDasharray="4 3" />
        ))}

        {/* her harfte iki nokta arasını birleştiren fark bacağı */}
        {harfler.map((k, i) => (
          <line key={`b${k}`} x1={xKonum(i)} y1={yKonum(natural[k])}
                x2={xKonum(i)} y2={yKonum(adapted[k])}
                stroke={DISC_COLORS[k]} strokeWidth="1.5" strokeOpacity="0.35" />
        ))}

        {/* doğal: kesikli ve soluk — "temel hal" */}
        <polyline points={dogalNok} fill="none" stroke="#64748b" strokeWidth="2"
                  strokeDasharray="6 4" strokeLinejoin="round" />
        {/* uyarlanmış: dolu ve koyu — "şu anki hal" */}
        <polyline points={uyarNok} fill="none" stroke="#0f172a" strokeWidth="2.5"
                  strokeLinejoin="round" />

        {harfler.map((k, i) => {
          const fark = Math.round(adapted[k] - natural[k]);
          const uyarY = yKonum(adapted[k]);
          const dogalY = yKonum(natural[k]);
          // fark etiketi iki noktanın ortasına, çakışmayı önlemek için sağa kaydırılır
          const ortaY = (uyarY + dogalY) / 2;
          return (
            <g key={k}>
              <circle cx={xKonum(i)} cy={dogalY} r="4.5" fill="#fff" stroke="#64748b" strokeWidth="2" />
              <circle cx={xKonum(i)} cy={uyarY} r="5" fill={DISC_COLORS[k]} stroke="#0f172a" strokeWidth="1.5" />

              <text x={xKonum(i)} y={dogalY + (dogalY > uyarY ? 16 : -9)} textAnchor="middle"
                    fontSize="11" fontWeight="600" fill="#64748b">{natural[k]}</text>
              <text x={xKonum(i)} y={uyarY + (uyarY > dogalY ? 17 : -10)} textAnchor="middle"
                    fontSize="12" fontWeight="800" fill="#0f172a">{adapted[k]}</text>

              {fark !== 0 && (() => {
                // Son sütunda etiket sağa taşıyordu; orada sola yaslanır.
                const saga = i < harfler.length - 1;
                return (
                  <text x={xKonum(i) + (saga ? 26 : -26)} y={ortaY + 4}
                        textAnchor={saga ? "start" : "end"}
                        fontSize="11" fontWeight="800"
                        fill={fark > 0 ? "#b45309" : "#1d4ed8"}>
                    {fark > 0 ? "▲" : "▼"}{Math.abs(fark)}
                  </text>
                );
              })()}

              {/* x ekseni harf etiketi */}
              <text x={xKonum(i)} y={Y - altB + 22} textAnchor="middle"
                    fontSize="15" fontWeight="800" fill={DISC_COLORS[k]}>{k}</text>
            </g>
          );
        })}

        <line x1={solB} y1={ustB + cizY} x2={G - sagB} y2={ustB + cizY} stroke="#cbd5e1" strokeWidth="1.5" />
      </svg>

      <div style={styles.chartLegend}>
        <span style={styles.legendItem}>
          <svg width="22" height="8" style={{ verticalAlign: "middle" }}>
            <line x1="0" y1="4" x2="22" y2="4" stroke="#0f172a" strokeWidth="2.5" />
          </svg>{" "}
          {text.comboAdapted}
        </span>
        <span style={styles.legendItem}>
          <svg width="22" height="8" style={{ verticalAlign: "middle" }}>
            <line x1="0" y1="4" x2="22" y2="4" stroke="#64748b" strokeWidth="2" strokeDasharray="6 4" />
          </svg>{" "}
          {text.comboNatural}
        </span>
        <span style={{ ...styles.legendItem, color: "#b45309" }}>▲ {text.comboUp}</span>
        <span style={{ ...styles.legendItem, color: "#1d4ed8" }}>▼ {text.comboDown}</span>
      </div>
      <p style={styles.chartNote}>{text.comboNote}</p>
    </figure>
  );
}

function DifferenceTable({ rows, text, tr }) {
  return (
    <div style={styles.tableWrap}>
      <div style={styles.tableHeader}>
        <span>DISC</span>
        <span>{text.naturalColumn}</span>
        <span>{text.adaptedColumn}</span>
        <span>{text.deltaColumn}</span>
        <span>{text.readingColumn}</span>
      </div>
      {rows.map((row) => (
        <div key={row.key} style={styles.tableRow}>
          <span style={{ ...styles.discBadgeSmall, background: DISC_COLORS[row.key] }}>{row.key}</span>
          <strong>{tr ? `%${row.natural}` : `${row.natural}%`}</strong>
          <strong>{tr ? `%${row.adapted}` : `${row.adapted}%`}</strong>
          <strong style={{ color: DIFFERENCE_COLORS[row.distance.key] }}>{row.delta > 0 ? "+" : ""}{row.delta}</strong>
          <span style={styles.tableText}>{row.text}</span>
        </div>
      ))}
    </div>
  );
}

function DimensionCard({ section, lang }) {
  const adaptedLabel = lang === "tr" ? "U" : "A";
  const naturalLabel = lang === "tr" ? "D" : "N";

  return (
    <article style={{ ...styles.dimensionCard, borderTopColor: DISC_COLORS[section.key] }}>
      <div style={styles.dimensionTop}>
        <span style={{ ...styles.discBadge, background: DISC_COLORS[section.key] }}>{section.key}</span>
        <div>
          <strong style={styles.dimensionName}>{section.content.title}</strong>
          {section.content.styleName ? (
            <div style={styles.dimensionStyleName}>{section.content.styleName}</div>
          ) : null}
          <div style={styles.dimensionScores}>
            {adaptedLabel} {section.adapted}% · {naturalLabel} {section.natural}% · Δ {section.delta > 0 ? "+" : ""}{section.delta}
          </div>
        </div>
      </div>
      <p style={styles.smallText}>{section.content.headline}</p>
      <p style={styles.smallText}>{section.content.interpretation}</p>
      <p style={styles.smallText}>{section.content.natural}</p>
      <p style={{ ...styles.deltaText, color: DIFFERENCE_COLORS[section.distance.key] }}>{section.content.delta}</p>
    </article>
  );
}

function CommunicationCard({ item, text }) {
  return (
    <article style={{ ...styles.communicationCard, borderTopColor: DISC_COLORS[item.key] }}>
      <div style={styles.communicationTop}>
        <span style={{ ...styles.discBadge, background: DISC_COLORS[item.key] }}>{item.key}</span>
        <h4 style={styles.cardTitle}>{item.title}</h4>
      </div>
      <CommunicationLine label={text.needLabel} value={item.need} />
      <CommunicationLine label={text.doLabel} value={item.do} />
      <CommunicationLine label={text.avoidLabel} value={item.avoid} />
      <div style={styles.personalAdjustment}>
        <strong>{text.personalLabel}</strong>
        <p>{item.personalised}</p>
      </div>
    </article>
  );
}

function CommunicationLine({ label, value }) {
  return (
    <p style={styles.communicationLine}>
      <strong>{label}: </strong>{value}
    </p>
  );
}

function DevelopmentPlan({ items }) {
  return (
    <div style={styles.timeline}>
      {items.map((item, index) => (
        <div key={index} style={styles.timelineItem}>
          <span style={styles.timelineNumber}>{index + 1}</span>
          <p style={styles.timelineText}>{item.replace(/^\d\. Hafta:\s*|^Week \d:\s*/, "")}</p>
        </div>
      ))}
    </div>
  );
}

function DiscUseNotes({ text }) {
  return (
    <Card title={text.useTitle}>
      <ul style={styles.list}>
        {text.safeItems.map((item) => <li key={item} style={styles.listItem}>{item}</li>)}
      </ul>
    </Card>
  );
}

const styles = {
  eksenUstEtiket: { textAlign: "center", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.06em", color: "#5b6473", padding: "6px 0" },
  eksenSatir: { display: "flex", alignItems: "center", gap: 8 },
  eksenYanEtiket: { flex: "0 0 auto", width: 74, fontSize: 10, fontWeight: 800, color: "#5b6473", textAlign: "center", lineHeight: 1.3 },
  ceyrekIzgara: { flex: "1 1 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, borderLeft: "2px solid #142033", borderTop: "2px solid #142033", paddingLeft: 8, paddingTop: 8 },
  ceyrekHucre: { padding: "9px 10px", borderRadius: 10, border: "1px solid #d7e1e7", background: "#fff" },
  ceyrekUst: { display: "flex", alignItems: "center", gap: 7, marginBottom: 5 },
  ceyrekRozet: { width: 24, height: 24, borderRadius: 7, fontWeight: 900, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" },
  ceyrekAd: { fontSize: 13, color: "#142033" },
  ceyrekObje: { marginLeft: "auto", display: "inline-flex", alignItems: "center" },
  ceyrekEksen: { fontSize: 11, color: "#142033", lineHeight: 1.4 },
  ceyrekAnahtar: { fontSize: 10.5, fontWeight: 800, color: "#5b6473", marginTop: 3 },
  ceyrekSes: { marginTop: 6, fontSize: 10.5, fontStyle: "italic", color: "#142033", borderLeft: "3px solid", paddingLeft: 6 },
  pairRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "start" },
  miniWrap: { minWidth: 0 },
  miniTitle: { fontSize: 12, fontWeight: 800, marginBottom: 6, textAlign: "center" },
  chartWrap: { margin: "14px 0 0", padding: "14px 16px 12px", borderRadius: 14, border: "1px solid #e5e7eb", background: "#fff" },
  chartTitle: { fontSize: 13, fontWeight: 800, color: "#08060d", marginBottom: 10 },
  chartLegend: { display: "flex", flexWrap: "wrap", gap: 14, marginTop: 10, fontSize: 11.5, color: "#4b5563", fontWeight: 600 },
  legendItem: { display: "inline-flex", alignItems: "center", gap: 4 },
  chartNote: { margin: "8px 0 0", fontSize: 11, lineHeight: 1.5, color: "#6b7280" },
  patternHeading: { margin: "0 0 8px", fontSize: 15, fontWeight: 800, color: "#b45309" },
  shell: { marginTop: 20, textAlign: "left", color: "#111827" },
  reportPage: { padding: "18px 0 8px", minHeight: 0 },
  pageHeader: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, borderBottom: "2px solid #111827", paddingBottom: 10, marginBottom: 16 },
  pageKicker: { display: "block", fontSize: 10, fontWeight: 900, letterSpacing: "0.13em", color: "#6b7280", textTransform: "uppercase" },
  pageTitle: { margin: "5px 0 0", fontSize: 23, lineHeight: 1.18, color: "#111827", letterSpacing: 0 },
  pageNumber: { flex: "0 0 auto", fontSize: 11, fontWeight: 800, color: "#6b7280", paddingTop: 3 },
  algiEtiket: {
    margin: "10px 0 2px",
    fontSize: 9.5,
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#7c8798",
  },
  algiMetin: { margin: 0, fontSize: 12.5, lineHeight: 1.55, color: "#142033" },
  pageLead: { margin: "0 0 14px", maxWidth: "78ch", fontSize: 14, lineHeight: 1.65, color: "#374151" },
  hero: { padding: "24px 26px", background: "#111827", color: "#fff", marginBottom: 12 },
  eyebrow: { margin: "0 0 9px", fontSize: 11, letterSpacing: "0.14em", fontWeight: 900, opacity: 0.86 },
  title: { margin: "0 0 10px", fontSize: 29, lineHeight: 1.18, letterSpacing: 0, color: "#fff" },
  intro: { margin: 0, maxWidth: "80ch", fontSize: 14, lineHeight: 1.62, opacity: 0.92 },
  metaGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8, marginBottom: 14 },
  meta: { display: "grid", gap: 3, borderBottom: "1px solid #d1d5db", padding: "8px 2px" },
  metaLabel: { fontSize: 10, fontWeight: 800, color: "#6b7280", textTransform: "uppercase" },
  signature: { borderLeft: "5px solid #111827", background: "#f3f4f6", padding: "14px 16px", marginBottom: 12 },
  signatureLabel: { display: "block", color: "#6b7280", fontSize: 10, fontWeight: 800, textTransform: "uppercase", marginBottom: 5 },
  signatureTitle: { display: "block", fontSize: 17, lineHeight: 1.3, marginBottom: 11 },
  signalRow: { display: "flex", flexWrap: "wrap", gap: 7 },
  signalChip: { display: "inline-flex", alignItems: "center", gap: 7, minHeight: 32, border: "1px solid", background: "#fff", padding: "4px 9px 4px 5px" },
  signalDot: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 23, height: 23, borderRadius: 999, color: "#fff", fontSize: 11, fontWeight: 900 },
  nearMark: { color: "#6b7280", fontWeight: 900 },
  twoColumn: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10, marginBottom: 10 },
  architectureGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10, marginBottom: 10 },
  architectureCard: { border: "1px solid #dfe3e8", borderTop: "3px solid #111827", borderRadius: 8, padding: 13, background: "#fff" },
  architectureKey: { display: "block", marginBottom: 7, fontSize: 11, fontWeight: 900, color: "#b45309", textTransform: "uppercase" },
  architectureBody: { margin: 0, color: "#374151", lineHeight: 1.55, fontSize: 12.5 },
  scenarioGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 },
  scenarioCard: { border: "1px solid #dfe3e8", borderRadius: 8, padding: 13, background: "#fff", minHeight: 154 },
  scenarioText: { margin: "0 0 9px", color: "#374151", lineHeight: 1.5, fontSize: 12.3 },
  scenarioFocus: { margin: 0, padding: "8px 9px", background: "#f3f4f6", color: "#111827", lineHeight: 1.45, fontSize: 11.8, fontWeight: 700 },
  card: { background: "#fff", border: "1px solid #dfe3e8", borderRadius: 8, padding: 14, marginBottom: 10, boxShadow: "0 4px 14px rgba(15, 23, 42, 0.045)" },
  featuredCard: { background: "#fff8e8", borderColor: "#e8c463" },
  cardTitle: { margin: "0 0 8px", fontSize: 15, lineHeight: 1.3, color: "#111827", letterSpacing: 0 },
  paragraph: { margin: 0, color: "#374151", lineHeight: 1.62, fontSize: 13.5 },
  list: { margin: 0, paddingLeft: 18, color: "#374151", lineHeight: 1.54, fontSize: 13 },
  listItem: { marginBottom: 6 },
  bandGrid: { display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", border: "1px solid #d1d5db", marginBottom: 12 },
  bandItem: { display: "grid", gap: 4, padding: "10px 8px", borderRight: "1px solid #d1d5db", minHeight: 54 },
  bandRange: { fontSize: 13, color: "#111827" },
  bandLabel: { fontSize: 11, color: "#6b7280" },
  ruleGrid: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10, marginBottom: 12 },
  ruleCard: { position: "relative", border: "1px solid #dfe3e8", borderRadius: 8, padding: "13px 13px 12px", background: "#fff" },
  ruleNumber: { display: "block", marginBottom: 8, color: "#b45309", fontSize: 11, fontWeight: 900 },
  callout: { borderLeft: "4px solid #2563eb", padding: "11px 14px", background: "#eff6ff", marginTop: 4 },
  calloutTitle: { display: "block", marginBottom: 4, fontSize: 13 },
  calloutText: { margin: 0, fontSize: 12.5, color: "#374151", lineHeight: 1.55 },
  scorePanel: { background: "#fff", border: "1px solid #dfe3e8", borderRadius: 8, padding: 14, marginBottom: 10 },
  scoreRow: { display: "grid", gridTemplateColumns: "34px 1fr 48px", gap: 9, alignItems: "center", marginBottom: 9 },
  discBadge: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 999, color: "#fff", fontWeight: 900, flex: "0 0 auto" },
  discBadgeSmall: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, borderRadius: 999, color: "#fff", fontWeight: 900, fontSize: 11 },
  scoreTrack: { height: 10, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" },
  scoreFill: { height: "100%", borderRadius: 999 },
  scoreValue: { textAlign: "right", fontSize: 13 },
  tableWrap: { border: "1px solid #d1d5db", marginTop: 10 },
  tableHeader: { display: "grid", gridTemplateColumns: "48px 74px 84px 58px 1fr", alignItems: "center", gap: 8, padding: "8px 10px", background: "#111827", color: "#fff", fontSize: 10, fontWeight: 800, textTransform: "uppercase" },
  tableRow: { display: "grid", gridTemplateColumns: "48px 74px 84px 58px 1fr", alignItems: "center", gap: 8, padding: "9px 10px", borderTop: "1px solid #e5e7eb", fontSize: 12.5 },
  tableText: { color: "#4b5563", lineHeight: 1.4 },
  dimensionGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 },
  dimensionCard: { border: "1px solid #dfe3e8", borderTop: "4px solid", borderRadius: 8, padding: 13, background: "#fff" },
  dimensionTop: { display: "flex", gap: 9, alignItems: "center", marginBottom: 9 },
  takimKutu: { marginTop: 20, padding: "20px 22px", background: "#f7f3ea", borderRadius: 14, border: "1px solid #e6e0d2" },
  takimUst: { margin: 0, fontSize: 9.5, fontWeight: 800, letterSpacing: ".12em", color: "#2fb8c6" },
  takimBaslik: { margin: "6px 0 8px", fontSize: 15, color: "#0e2a3f" },
  takimIntro: { margin: "0 0 14px", fontSize: 11.5, lineHeight: 1.55, color: "#5b6473" },
  takimIzgara: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 },
  takimKart: { background: "#fff", borderRadius: 10, padding: "12px 14px", borderTop: "3px solid #ccc", border: "1px solid #eceff3" },
  takimKartBaslik: { display: "block", fontSize: 11.5, color: "#0e2a3f", marginBottom: 5 },
  takimKartMetin: { margin: 0, fontSize: 10.8, lineHeight: 1.5, color: "#142033" },
  takimGerilim: { margin: "8px 0 0", fontSize: 10.4, lineHeight: 1.5, color: "#5b6473", fontStyle: "italic" },
  takimSoru: { margin: "14px 0 0", fontSize: 11.5, color: "#0e2a3f", fontWeight: 700 },
  takimSoruEtiket: { display: "block", fontSize: 9.5, fontWeight: 800, letterSpacing: ".08em", color: "#2fb8c6", marginBottom: 3 },
  dimensionStyleName: { fontSize: 10.5, color: "#5b6473", fontWeight: 600, marginTop: 1 },
  dimensionName: { display: "block", fontSize: 13.5, lineHeight: 1.28 },
  dimensionScores: { marginTop: 2, fontSize: 11, color: "#6b7280", fontWeight: 700 },
  smallText: { margin: "0 0 7px", fontSize: 12.5, lineHeight: 1.5, color: "#4b5563" },
  deltaText: { margin: 0, fontSize: 12, lineHeight: 1.45, fontWeight: 800 },
  communicationGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 },
  communicationCard: { border: "1px solid #dfe3e8", borderTop: "4px solid", borderRadius: 8, padding: 12, background: "#fff" },
  communicationTop: { display: "flex", alignItems: "center", gap: 9, marginBottom: 8 },
  communicationLine: { margin: "0 0 6px", fontSize: 12.5, lineHeight: 1.44, color: "#374151" },
  personalAdjustment: { marginTop: 8, padding: "8px 9px", background: "#f3f4f6", fontSize: 12, lineHeight: 1.42 },
  timeline: { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 9, marginBottom: 12 },
  timelineItem: { borderTop: "3px solid #111827", padding: "10px 9px", background: "#f3f4f6", minHeight: 112 },
  timelineNumber: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, borderRadius: 999, background: "#111827", color: "#fff", fontSize: 11, fontWeight: 900 },
  timelineText: { margin: "9px 0 0", fontSize: 12.5, lineHeight: 1.48, color: "#374151" },
  overviewWrap: { marginBottom: 14 },
  overviewTitle: { margin: "4px 0 5px", fontSize: 17, lineHeight: 1.25 },
  overviewIntro: { margin: "0 0 10px", fontSize: 13, lineHeight: 1.5, color: "#4b5563" },
  overviewGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 },
  overviewItem: { display: "flex", alignItems: "flex-start", gap: 9, borderBottom: "1px solid #dfe3e8", padding: "8px 0" },
  overviewName: { display: "block", fontSize: 13 },
  overviewDescription: { margin: "2px 0 0", fontSize: 12.5, color: "#4b5563", lineHeight: 1.42 },
  note: { margin: "8px 0 0", padding: 11, background: "#f3f4f6", borderLeft: "4px solid #111827", color: "#4b5563", fontSize: 12, lineHeight: 1.5 }
};
